import React, { createContext, useContext, useState, useEffect } from 'react';
import { authAPI } from '../services/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    checkAuthStatus();
  }, []);

  const checkAuthStatus = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      if (token) {
        const response = await authAPI.getUser();
        if (response.status === 'success') {
          const userData = response.data.user;
          setUser(userData);
          setIsAuthenticated(true);
        }
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      localStorage.removeItem('admin_token');
      localStorage.removeItem('admin_user');
      setUser(null);
      setIsAuthenticated(false);
    } finally {
      setLoading(false);
    }
  };

  const login = async (credentials) => {
    try {
      // Mark this as an admin-portal login so the API can enforce admin-only access
      const response = await authAPI.login({
        ...credentials,
        admin_login: true,
      });
      
      if (response.status === 'success') {
        const token = response.data.token;
        const userData = response.data.user;

        // Rely on the API to enforce admin-only access based on admin_login flag
        localStorage.setItem('admin_token', token);
        localStorage.setItem('admin_user', JSON.stringify(userData));
        
        setUser(userData);
        setIsAuthenticated(true);
        
        return { success: true, user: userData };
      } else if (response.status === 'otp_required') {
        return { 
          success: false, 
          requiresOtp: true, 
          otpData: response.data,
          message: response.message 
        };
      } else {
        return { success: false, message: response.message || 'Login failed' };
      }
    } catch (error) {
      console.error('Login error:', error);
      return { 
        success: false, 
        message: error.response?.data?.message || 'Login failed' 
      };
    }
  };

  const verifyOtp = async (otpData) => {
    try {
      // Mark this as an admin-portal OTP verification
      const response = await authAPI.verifyOtp({
        ...otpData,
        admin_login: true,
      });
      
      if (response.status === 'success') {
        const token = response.data.token;
        const userData = response.data.user;

        // Rely on the API to enforce admin-only access based on admin_login flag
        localStorage.setItem('admin_token', token);
        localStorage.setItem('admin_user', JSON.stringify(userData));
        
        setUser(userData);
        setIsAuthenticated(true);
        
        return { success: true, user: userData };
      } else {
        return { 
          success: false, 
          message: response.message || 'OTP verification failed',
          code: response.code || null,
          remainingAttempts: response.remaining_attempts || null
        };
      }
    } catch (error) {
      console.error('OTP verification error:', error);
      const errorData = error.response?.data;
      return { 
        success: false, 
        message: errorData?.message || 'OTP verification failed',
        code: errorData?.code || null,
        remainingAttempts: errorData?.remaining_attempts || null
      };
    }
  };

  const resendOtp = async (otpData) => {
    try {
      // Mark this as an admin-portal OTP resend
      const response = await authAPI.resendOtp({
        ...otpData,
        admin_login: true,
      });
      
      if (response.status === 'success') {
        return { success: true, data: response.data };
      } else {
        return { 
          success: false, 
          message: response.message || 'Failed to resend OTP'
        };
      }
    } catch (error) {
      console.error('OTP resend error:', error);
      const errorData = error.response?.data;
      return { 
        success: false, 
        message: errorData?.message || 'Failed to resend OTP'
      };
    }
  };

  const logout = async () => {
    try {
      await authAPI.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('admin_token');
      localStorage.removeItem('admin_user');
      setUser(null);
      setIsAuthenticated(false);
    }
  };

  const value = {
    user,
    isAuthenticated,
    loading,
    login,
    logout,
    verifyOtp,
    resendOtp,
    checkAuthStatus
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext; 